import { cloneElement, ReactElement } from "react";

import { alpha, Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ChevronRight } from "components/icons";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useTranslation } from "react-i18next";

interface IProps {
  color: string;
  icon: ReactElement;
  exchangeTitle: string;
  exchangeConnectionLabel?: string | null;
  currencyPair: string;
  label?: string | null;
  detailLabel: string;
  onDetail: () => void;
}

const LinkedStrategyCardTypography = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

const LinkedStrategyCardBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>(({ color, theme }) => ({
  backgroundColor: alpha(color, 0.1),
  borderRadius: "10px",
  color,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  gap: theme.spacing(1),
  width: "100%",
}));

const LinkedStrategyCard = ({
  color,
  icon,
  exchangeTitle,
  exchangeConnectionLabel,
  currencyPair,
  label,
  detailLabel,
  onDetail,
}: IProps) => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();

  return (
    <LinkedStrategyCardBox color={color}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto auto minmax(0, 1fr)",
          columnGap: 1,
          alignItems: "center",
          minWidth: 0,
          flex: 1,
          color,
        }}>
        {cloneElement(icon, {
          sx: { gridRow: "1 / span 2", fontSize: { xs: 18, sm: 24 }, color: "inherit" },
        })}
        <LinkedStrategyCardTypography fontWeight={600} color="inherit" noWrap>
          {exchangeTitle}
        </LinkedStrategyCardTypography>
        <LinkedStrategyCardTypography color="inherit" noWrap>
          {exchangeConnectionLabel}
        </LinkedStrategyCardTypography>
        <LinkedStrategyCardTypography fontWeight={600} color="inherit" noWrap>
          {currencyPair}
        </LinkedStrategyCardTypography>
        <LinkedStrategyCardTypography color={label ? "inherit" : alpha(color, 0.55)} noWrap>
          {label || t("withoutLabel")}
        </LinkedStrategyCardTypography>
      </Box>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ChevronRight fontSize="small" />}
        onClick={onDetail}
        sx={{
          flexShrink: 0,
          backgroundColor: colors.white,
          border: `1px solid ${alpha(color, 0.2)}`,
          color,
          "&:hover": {
            backgroundColor: colors.gray50,
            borderColor: alpha(color, 0.35),
          },
        }}>
        {detailLabel}
      </Button>
    </LinkedStrategyCardBox>
  );
};

export default LinkedStrategyCard;
